import { useCallback, useEffect, useState } from "react";
import {
  parseThemeMode,
  resolveTheme,
  THEME_STORAGE_KEY,
  type Theme,
  type ThemeMode,
  toggleTheme,
} from "../core/theme";

const DARK_QUERY = "(prefers-color-scheme: dark)";

function readStoredMode(): ThemeMode {
  try {
    return parseThemeMode(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return "system";
  }
}

function storeMode(mode: ThemeMode): void {
  try {
    if (mode === "system") localStorage.removeItem(THEME_STORAGE_KEY);
    else localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // Storage may be blocked; the choice then lasts for this page only.
  }
}

/**
 * Owns the theme side effects: localStorage, `prefers-color-scheme`, and
 * `<html data-theme>` (initially set by the pre-paint script in index.html).
 */
export function useTheme(): { mode: ThemeMode; theme: Theme; toggle: () => void } {
  const [mode, setMode] = useState(readStoredMode);
  const [prefersDark, setPrefersDark] = useState(() => matchMedia(DARK_QUERY).matches);
  const theme = resolveTheme(mode, prefersDark);

  useEffect(() => {
    const query = matchMedia(DARK_QUERY);
    const onChange = () => setPrefersDark(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  const toggle = useCallback(() => {
    const next = toggleTheme(mode, prefersDark);
    storeMode(next);
    setMode(next);
  }, [mode, prefersDark]);

  return { mode, theme, toggle };
}
